import React, { useEffect, useState } from "react";
import { Form, Select } from "antd";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/redux";
import { closeModal } from "../../../redux/features/whiteboard/whiteboardSlice";
import { FooterButtons, FormWrapper, StyledButton } from "../../../components/styles";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import StyledModal from "../../../components/CustomModal";
import { ResizableContainer } from "../../ReportDetails/ReportDetailsFooter/styles";
import { addWhiteboardItem, fetchWhiteboardItems, updateWhiteboardItem } from "../../../redux/features/whiteboard/whiteboardAsynkThunk";

const { Option } = Select;

const WhiteboardModal: React.FC = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { isModalOpen, currentItem, isEditing, loading, currentPage, pageSize } = useAppSelector((state) => state.whiteboard);
  const [messageContent, setMessageContent] = useState<string>("");

  useEffect(() => {
    if (isModalOpen && isEditing && currentItem) {
      form.setFieldsValue({
        message: currentItem.message,
        addedBy: currentItem.addedBy,
      });
      setMessageContent(currentItem.message);
    } else {
      form.resetFields();
      setMessageContent("");
    }
  }, [isModalOpen, isEditing, currentItem, form]);

  const handleCancel = () => {
    dispatch(closeModal());
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      if (isEditing && currentItem) {
        dispatch(
          updateWhiteboardItem({
            id: currentItem.id,
            message: messageContent,
          })
        );
      } else {
        dispatch(addWhiteboardItem(messageContent));
        if (currentPage !== 1) {
          dispatch(fetchWhiteboardItems({ page: currentPage, pageSize }));
        }
      }
    } catch (error: any) {
      console.error("Failed to save whiteboard item:", error);
    }
  };

  return (
    <StyledModal
      draggable={true}
      title={isEditing ? "Edit Whiteboard Message" : "Add New Whiteboard Message"}
      open={isModalOpen}
      onCancel={handleCancel}
      width={700}
      footer={
        <FooterButtons>
          <StyledButton onClick={handleCancel} disabled={loading}>Cancel</StyledButton>
          <StyledButton type="primary" loading={loading} onClick={handleSubmit}>
            {isEditing ? "Update" : "Add"}
          </StyledButton>
        </FooterButtons>
      }
    >
      <FormWrapper form={form} layout="vertical" name="whiteboardForm" initialValues={{ addedBy: "Not Set" }}>
        <Form.Item
          name="message"
          label="Message"
          rules={[
            { required: true, message: "" },
            {
              validator: (_, value) => {
                const textContent = messageContent?.replace(/<[^>]*>/g, "").trim();
                if (!textContent) {
                  return Promise.reject(new Error("Please enter a message"));
                }
                return Promise.resolve();
              },
            },
          ]}
          validateTrigger={["onChange", "onBlur"]}
        >
          <ResizableContainer>
            {React.createElement(ReactQuill as any, {
              theme: "snow",
              style: { height: "100%" },
              readOnly: loading,
              onChange: (content: string) => {
                form.setFieldsValue({ message: content });
                setMessageContent(content);
                // Trigger validation when content changes
                form.validateFields(["message"]);
              },
              onBlur: () => {
                // Trigger validation when editor loses focus
                form.validateFields(["message"]);
              },
              value: form.getFieldValue("message") || "",
            })}
          </ResizableContainer>
        </Form.Item>

        {/* Admin info is automatically added by the API */}
      </FormWrapper>
    </StyledModal>
  );
};

export default WhiteboardModal;
